/**
 * useGatewayImage — one same-origin request per image, no browser-side gateway
 * fan-out, no CORP/COEP surprises.
 *
 * Earlier versions of this hook tried up to 6 public IPFS gateways directly
 * from the browser. That fixed individual broken images but meant a grid of N
 * NFT thumbnails could fire 6N concurrent cross-origin requests — which is
 * what got `ipfs.io` itself to start 403ing us in bulk (its own abuse
 * protection reacting to the burst), on top of Telegram's Mini App WebView
 * blocking plain cross-origin `<img>` loads outright under COEP.
 *
 * The fix: never talk to a public gateway from the browser. `resolveMediaUrl`
 * turns IPFS media into `/ipfs-proxy/<cid>` — always same-origin, so neither
 * CORP nor COEP ever come up — and the server behind that path tries the
 * gateways once and caches the result (IPFS content is hash-addressed, so
 * caching it forever is safe). One request per image, most of which are
 * cache hits after the first view anywhere.
 */

import { useEffect, useState } from 'react'
import { resolveMediaUrl } from '@properwallet/chain-eos'

export function useGatewayImage(url: string | null | undefined) {
  const [failed, setFailed] = useState(false)

  useEffect(() => {
    setFailed(false)
  }, [url])

  const src = url ? resolveMediaUrl(url) : null

  return {
    src: failed ? null : src,
    failed,
    onError: () => setFailed(true),
    /** Kept for call-site compatibility; same-origin media needs no CORS mode. */
    imgProps: {} as { crossOrigin?: 'anonymous' },
  }
}
